//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React from 'react';
import { RegisterOptions } from 'react-hook-form';
import styled from 'styled-components';

import { FileUploader } from '@campaigns/components/shared/file-uploader/FileUploader';
import { FormField } from '@campaigns/components/shared/product-details-modal/drawer/form/FormField';
import { MAX_IMAGE_SIZE_BYTES } from '@campaigns/constants/SamplingImageConstants';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useImageUpload } from '@campaigns/hooks/useImageUpload';
import { Alert } from '@components/atoms/alert/Alert';
import { BodyText } from '@components/atoms/text/Text';
import { isDefined } from '@utils/filterPredicates';
import { SUPPORTED_IMAGE_MIME_TYPES } from '@utils/mimeTypes';

export interface UploadTabProps {
  index: number;
  onClearImage: () => void;
  onClosePopover: () => void;
  onFileSelect: (file: File | undefined) => void;
  rules: RegisterOptions | undefined;
}

export const UploadTab = ({
  index,
  onClearImage,
  onClosePopover,
  onFileSelect,
  rules,
}: UploadTabProps) => {
  const { error: imageUploadError, handleFilesSelected } = useImageUpload({
    onClearImage,
    onFileSelect: (file) => {
      onFileSelect(file);
      onClosePopover();
    },
  });

  return (
    <Container>
      {/* 
        React Hook Form limitation: When a field is removed from the DOM, its validation state is lost.
        We keep the URL input field hidden (but in the DOM) when on Upload tab to maintain the form's
        validation state, allowing the ButtonPopover to show validation errors consistently across
        tab switches.
      */}
      <HiddenFormField>
        <FormField
          inputType='textarea'
          label='Image web address'
          name={`variations.${index}.imgUrl`}
          rules={rules}
        />
      </HiddenFormField>
      {isDefined(imageUploadError) && (
        <Alert type='error'>{imageUploadError}</Alert>
      )}
      <FileUploader
        actionText='Click to select'
        existingAssetUrls={[]}
        maxTotalSize={MAX_IMAGE_SIZE_BYTES}
        onFilesSelected={handleFilesSelected}
        shouldAcceptMultipleFiles={false}
        supportedFileTypes={SUPPORTED_IMAGE_MIME_TYPES}
        title={<BodyText>Upload image</BodyText>}
        trackingId={
          SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_INPUT_FIELD_UPLOAD_IMAGE_VARIANT
        }
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const HiddenFormField = styled.div`
  display: none;
`;
